import { ReactNode, SVGProps } from 'react'

/**
 * Outline SVG icons for sidebar nav items, one per modulesList moduleId.
 * Visual style mirrors the redesign reference (sidebar.html):
 *   stroke 1.8, currentColor, viewBox 24, lucide-ish line-art.
 */

const baseProps = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const

/* 1 — הזמנות (Orders): clipboard with check */
const OrdersIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <rect x='5' y='4' width='14' height='17' rx='2' />
    <path d='M9 4h6v3H9z' />
    <path d='m9 13 2 2 4-4' />
  </svg>
)

/* 2 — תעודות משלוח למחסן (Warehouse shipping docs): inbound box */
const WarehouseInboundIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M3 9V20a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9' />
    <path d='M2 9 4 4h16l2 5' />
    <path d='M12 13v6m-3-3 3 3 3-3' />
  </svg>
)

/* 3 — אריזות (Packaging): package box */
const PackageIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' />
    <polyline points='3.27 6.96 12 12.01 20.73 6.96' />
    <line x1='12' y1='22.08' x2='12' y2='12' />
  </svg>
)

/* 4 — עבודה במחסן (Warehouse work): factory */
const WarehouseWorkIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M3 21V11l5 3V11l5 3V11l5 3v7Z' />
    <path d='M3 21h18' />
    <path d='M9 17h2M14 17h2' />
  </svg>
)

/* 5 — תעודות משלוח (Shipping docs): document with truck */
const ShippingDocIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z' />
    <path d='M14 3v5h5' />
    <path d='M9 13h6M9 17h4' />
  </svg>
)

/* 6 — הפצה (Distribution): truck */
const DistributionIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M1 8h11v9H1z' />
    <path d='M12 11h5l3 3v3h-8z' />
    <circle cx='5.5' cy='17.5' r='1.8' />
    <circle cx='16.5' cy='17.5' r='1.8' />
  </svg>
)

/* 7 — נתוני מכר (Sales data): bar chart */
const SalesDataIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <line x1='4' y1='20' x2='20' y2='20' />
    <rect x='5' y='10' width='3' height='10' />
    <rect x='10.5' y='6' width='3' height='14' />
    <rect x='16' y='13' width='3' height='7' />
  </svg>
)

/* 8 — תעודות משטח (Pallet docs): layers / pallet */
const PalletDocIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <polygon points='12 3 21 8 12 13 3 8' />
    <polyline points='3 14 12 19 21 14' />
    <polyline points='3 11 12 16 21 11' />
  </svg>
)

/* 9 — תעודות מיון (Sorting docs): funnel */
const SortDocIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M4 4h16l-6 8v6l-4 2v-8z' />
  </svg>
)

/* 10 — נהגים (Drivers): user circle */
const DriverIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <circle cx='12' cy='8' r='4' />
    <path d='M4 21c0-4.418 3.582-8 8-8s8 3.582 8 8' />
  </svg>
)

/* 11 — כניסה (Entry): barcode */
const EntryIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <line x1='4' y1='6' x2='4' y2='18' />
    <line x1='7' y1='6' x2='7' y2='18' />
    <line x1='10' y1='6' x2='10' y2='18' />
    <line x1='13' y1='6' x2='13' y2='18' />
    <line x1='16' y1='6' x2='16' y2='18' />
    <line x1='20' y1='6' x2='20' y2='18' />
  </svg>
)

/* 12 — שאילתות (Queries): database / search */
const QueriesIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <ellipse cx='12' cy='5' rx='8' ry='3' />
    <path d='M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5' />
    <path d='M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6' />
  </svg>
)

/* 13 — הנהלת חשבונות (Accounting): receipt */
const AccountingIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M5 3h14v18l-3-2-3 2-3-2-2 2-3-2Z' />
    <line x1='9' y1='8' x2='15' y2='8' />
    <line x1='9' y1='12' x2='15' y2='12' />
    <line x1='9' y1='16' x2='13' y2='16' />
  </svg>
)

/* 14 — ספירת מלאי (Inventory count): clipboard list */
const InventoryIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <rect x='5' y='4' width='14' height='17' rx='2' />
    <path d='M9 4h6v3H9z' />
    <line x1='9' y1='12' x2='15' y2='12' />
    <line x1='9' y1='16' x2='15' y2='16' />
    <circle cx='7.5' cy='12' r='0.5' fill='currentColor' stroke='none' />
    <circle cx='7.5' cy='16' r='0.5' fill='currentColor' stroke='none' />
  </svg>
)

/* 15 — משטחים (פרחים) (Pallets/Flowers): sprout */
const PalletsFlowersIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M12 21V11' />
    <path d='M12 11c0-3-3-5-6-5 0 3 3 5 6 5z' />
    <path d='M12 11c0-3 3-5 6-5 0 3-3 5-6 5z' />
    <path d='M5 21h14' />
  </svg>
)

/* 16 — WMS (Warehouse Management System): warehouse */
const WMSIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <path d='M3 21V9l9-6 9 6v12' />
    <path d='M3 21h18' />
    <rect x='8' y='13' width='8' height='8' />
    <line x1='12' y1='13' x2='12' y2='21' />
    <line x1='8' y1='17' x2='16' y2='17' />
  </svg>
)

/* 17 — טבלאות (Tables): grid */
const TablesIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <rect x='3' y='3' width='18' height='18' rx='2' />
    <line x1='3' y1='9' x2='21' y2='9' />
    <line x1='3' y1='15' x2='21' y2='15' />
    <line x1='9' y1='3' x2='9' y2='21' />
    <line x1='15' y1='3' x2='15' y2='21' />
  </svg>
)

/* Default fallback */
const DefaultIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <rect x='3' y='3' width='7' height='7' rx='1.5' />
    <rect x='14' y='3' width='7' height='7' rx='1.5' />
    <rect x='3' y='14' width='7' height='7' rx='1.5' />
    <rect x='14' y='14' width='7' height='7' rx='1.5' />
  </svg>
)

const MODULE_ICONS: Record<number, () => ReactNode> = {
  1: () => <OrdersIcon />,
  2: () => <WarehouseInboundIcon />,
  3: () => <PackageIcon />,
  4: () => <WarehouseWorkIcon />,
  5: () => <ShippingDocIcon />,
  6: () => <DistributionIcon />,
  7: () => <SalesDataIcon />,
  8: () => <PalletDocIcon />,
  9: () => <SortDocIcon />,
  10: () => <DriverIcon />,
  11: () => <EntryIcon />,
  12: () => <QueriesIcon />,
  13: () => <AccountingIcon />,
  14: () => <InventoryIcon />,
  15: () => <PalletsFlowersIcon />,
  16: () => <WMSIcon />,
  17: () => <TablesIcon />,
}

export const getModuleIcon = (moduleId: number): ReactNode => {
  const factory = MODULE_ICONS[moduleId]
  return factory ? factory() : <DefaultIcon />
}

/* Brand mark used in SidebarHeader's logo box. */
export const BrandMark = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} strokeWidth={2.4} {...p}>
    <path d='M12 3 L4 9 L12 15 L20 9 Z' />
    <path d='M4 15 L12 21 L20 15' />
  </svg>
)

/* Generic chevron used in nav-links and toggle button. */
export const ChevronRightIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} strokeWidth={2.2} {...p}>
    <polyline points='9 6 15 12 9 18' />
  </svg>
)

/* Test/play icon used for the developer test link. */
export const TestIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg {...baseProps} {...p}>
    <polygon points='5 3 19 12 5 21 5 3' />
  </svg>
)
